import { Platform, Alert } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import i18n from '../../i18n';
import { hungarianRoundNumbers } from './hungarianRoundNumbers';
import { generateHappyId } from './happyId';
import ErrorReportingService from './ErrorReportingService';

/**
 * Service for generating and sending QR codes to Fiscat cashier devices
 */
class FiscatQRService {
  /**
   * Generate a QR code string for a Fiscat cashier device
   * @param {Object} order - The order object
   * @returns {string} - The generated QR code string
   */
  static generateQRCodeString(order) {
    const { orderCode, items, paymentTypes, orderSum, paymentType } = order;

    // Validate that the order has items and a non-zero price
    if (!items || items.length === 0) {
      ErrorReportingService.info("Empty order detected, skipping QR code generation", { orderCode }, "FiscatQRService.generateQRCodeString");
      return null;
    }

    // Check if all items have zero price
    const hasNonZeroPrice = items.some(item => (item.remotePrice || item.price || 0) > 0);
    if (!hasNonZeroPrice) {
      ErrorReportingService.info("Order with zero price detected, skipping QR code generation", { orderCode }, "FiscatQRService.generateQRCodeString");
      return null;
    }

    const flattenedItems = items.flatMap(item => [item, ...(item.extras.filter(e => e.price > 0) || [])]);

    // Initialize the QR code string with the base URL and command
    let qrCodeString = '';

    const discountPercentage = order?.discounts?.percentage || 0;
    const serviceFees = {};

    const getVATCode = (vatPercentage) => {
      if (vatPercentage === 5) return 1;
      if (vatPercentage === 18) return 2;
      if (vatPercentage === 27) return 3;
      if (vatPercentage === 0) return 5;
    };

    let rowsCount = 0;

    // Add each item to the QR code string
    ([...items]).forEach(item => {
      const itemName = item.remoteName || item.name?.hu || item.name || 'GYŰJTŐ';
      const vatPercentage = item?.item?.vat[order.orderType] || 27;
      const vatCode = getVATCode(vatPercentage); // Get VAT code based on percentage
      const itemPrice = item.remotePrice || item.price || 0;
      const itemQuantity = item.amount || item.quantity || 1;

      // Calculate service fee for this item if applicable
      if (order.serviceFee > 0) {
        const itemServiceFee = ((itemPrice * itemQuantity) / 100) * order.serviceFee;

        // Add to the appropriate VAT category in serviceFees
        if (!serviceFees[vatPercentage]) {
          serviceFees[vatPercentage] = 0;
        }
        serviceFees[vatPercentage] += itemServiceFee;
      }

      rowsCount++;
      qrCodeString += `${itemName}|${vatCode}|${itemPrice}|${itemQuantity}|${discountPercentage > 0 ? "M"+discountPercentage : ""}|`;

      if(item.extras.length > 0){
        item.extras.forEach(extra => {
          const extraPrice = extra.price || 0;
          // Only include extras with price > 0
          if (extraPrice > 0) {
            const extraName = extra.remoteName || extra?.item?.name?.hu || 'GYŰJTŐ';
            rowsCount++;
            qrCodeString += ` +${extraName}|${vatCode}|${extraPrice}|${itemQuantity}|${discountPercentage > 0 ? "M"+discountPercentage : ""}|`;
          }
        });
      }
    });

    // Add discount if present
    if(order.discounts && order.discounts.amount > 0 || order.discountAmount || order.discountPrice){
      const vatCode = getVATCode(27); // Default VAT code for discount
      rowsCount++;
      //qrCodeString += `Kedvezmény|${vatCode}|-${order.discounts.amount || order.discountAmount || order.discountPrice}|1||`;
    }

    // Add service fee if present
    if(order.serviceFee > 0 || order.serviceFeePrice > 0){
      // Add service fee for each VAT rate
      Object.keys(serviceFees).forEach(vatRate => {
        const vatCode = getVATCode(Number(vatRate));
        const serviceFeeAmount = serviceFees[vatRate].toFixed(0);
        if (serviceFeeAmount > 0) {
          rowsCount++;
          qrCodeString += `Szervízdíj ${vatRate}%|${vatCode}|${serviceFeeAmount}|1||`;
        }
      });
    }

    // Add delivery fee if present
    if(order.deliveryFee && order.deliveryFee > 0){
      rowsCount++;
      qrCodeString += `Szállítási díj|3|${order.deliveryFee}|1||`;
    }

    // Create the full QR code string
    let fullQRCode = `fiscat/AEE|SLD||${orderCode.replace("-", "")}|6|Rendelés száma:|#${orderCode}|-------------|www.happygastro.hu|Üzemelteti a|Happy Gastro||||${rowsCount}|` + qrCodeString;

    // Add payment types and amounts
    if(order.paymentTypes && order.paymentTypes.length > 0) {
      order.paymentTypes.forEach(payment => {
        const paymentType = payment.paymentType === "cash" ? "P" : "N";
        const paymentAmount = (payment.paymentType === "cash" ? hungarianRoundNumbers(payment.amount) : payment.amount) || 0;

        fullQRCode += `${paymentType}|${paymentAmount}`;
      });
    } else {
      const paymentTypeCode = order.paymentType === "cash" ? "P" : "N";
      const paymentAmount = orderSum || '';
      fullQRCode += `${paymentTypeCode}|${order.paymentType === "cash" ? hungarianRoundNumbers(paymentAmount) : paymentAmount}`;
    }

    // QR code data logging has been removed as per requirements

    return fullQRCode;
  }

  /**
   * Send a QR code to the cashier device
   * @param {Object} order - The order object
   * @param {Function} sendData - Function to send data to the device
   * @returns {Promise<boolean>} - Whether the operation was successful
   */
  static async sendToCashier(order, sendData) {
    try {
      return new Promise(async (resolve, reject) => {
        const qrCodeString = this.generateQRCodeString(order);

        // If qrCodeString is null, it means the order was empty or had zero price
        if (!qrCodeString) {
          ErrorReportingService.info("Skipping empty or zero-price order", { orderCode: order.orderCode }, "FiscatQRService.sendToCashier");
          resolve(true); // Resolve successfully since we're intentionally skipping
          return;
        }

        ErrorReportingService.info("QR code generated", { qrCode: qrCodeString }, "FiscatQRService.sendToCashier");

        const paymentCashier = await AsyncStorage.getItem('cashierDevice');
        if (paymentCashier) {
          if (Platform.OS === "android") {
            try {
              // Import the triggerSendToCashier function
              const { triggerSendToCashier } = require('./BackgroundTasks');

              // Use the background task to send to cashier
              const result = await triggerSendToCashier(qrCodeString);

              if (result === true) {
                resolve(true);
              } else if (result && result.error) {
                // If there's an error, we'll show the retry dialog
                reject(result.error);
              } else {
                // Fallback to direct method if background task not available
                sendData(qrCodeString);
                resolve(true);
              }
            } catch (e) {
              reject(e);
            }
          } else {
            resolve(true); // Non-Android platforms don't need cashier communication
          }
        } else {
          resolve(true); // No cashier device configured
        }
      });
    } catch (e) {
      ErrorReportingService.error("Error sending to cashier", { error: e }, "FiscatQRService.sendToCashier");
      await ErrorReportingService.reportError(e, { source: "FiscatQRService.sendToCashier" });
      return Promise.reject(e);
    }
  }

  /**
   * Show a retry dialog for failed cashier communication
   * @param {Function} attemptSendToCashier - Function to retry sending to cashier
   * @returns {Promise<boolean>} - Whether the operation was successful
   */
  static showRetryDialog(attemptSendToCashier) {
    return new Promise((resolve, reject) => {
      if (Platform.OS === "android") {
        Alert.alert(
          i18n.t('error'),
          i18n.t('cashier_communication_failed'),
          [
            {
              text: i18n.t('cancel'),
              onPress: () => {
                ErrorReportingService.info('Cashier communication cancelled', {}, 'FiscatQRService.showRetryDialog');
                resolve(false); // Resolve with false to indicate cancellation
              },
              style: 'cancel',
            },
            {
              text: i18n.t('retry'),
              onPress: () => {
                ErrorReportingService.info('Retrying cashier communication', {}, 'FiscatQRService.showRetryDialog');
                attemptSendToCashier()
                  .then(result => resolve(result))
                  .catch(e => {
                    ErrorReportingService.error('Retry failed', { error: e }, 'FiscatQRService.showRetryDialog');
                    ErrorReportingService.reportError(e, { source: 'FiscatQRService.showRetryDialog' });
                    this.showRetryDialog(attemptSendToCashier).then(result => resolve(result));
                  });
              },
            },
          ],
          { cancelable: false }
        );
      } else {
        // For iOS or other platforms
        alert(i18n.t('cashier_communication_failed'));
        resolve(false); // Resolve with false to indicate failure
      }
    });
  }

}

export default FiscatQRService;
